// ==========================================
// Glance popover (4b)
// ==========================================
// The web equivalent of the design's tray / menu-bar popover: 300x220, anchored to a
// launcher in the ERP header. Closed-panel glance only — streak, daily quest, two
// quick actions. No chat log.
//
// Wired the same way as <app-activity-panel [isOpen] (onClose)> already in the header.
// ==========================================

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BbState, BuddyProgress } from '../models/buddy.types';
import { ACCENT_HEX } from '../models/buddy.constants';
import { COMPANION_NAME, MOCK_GLANCE_STATUS, MOCK_QUICK_ACTIONS, QuickAction } from '../services/buddy-mock.data';
import { BbStateService } from '../services/bb-state.service';
import { BuddyProgressService } from '../services/buddy-progress.service';
import { BuddyStateService } from '../services/buddy-state.service';
import { BuddyWorkflowService } from '../services/buddy-workflow.service';

@Component({
  selector: 'app-buddy-glance',
  standalone: false,
  templateUrl: './buddy-glance.component.html',
  styleUrls: ['./buddy-glance.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuddyGlanceComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;

  // Named to match the header's existing activity-panel contract.
  @Output() onClose = new EventEmitter<void>();

  readonly companionName = COMPANION_NAME;
  readonly status = MOCK_GLANCE_STATUS;
  readonly quickActions = MOCK_QUICK_ACTIONS;

  progress!: BuddyProgress;
  bbState: BbState = 'idle';

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly progressService: BuddyProgressService,
    private readonly shell: BuddyStateService,
    private readonly workflows: BuddyWorkflowService,
    private readonly bb: BbStateService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.progress = this.progressService.current;
  }

  ngOnInit(): void {
    this.progressService.progress.pipe(takeUntil(this.destroyed$)).subscribe((p) => {
      this.progress = p;
      this.cdr.markForCheck();
    });
    this.bb.state.pipe(takeUntil(this.destroyed$)).subscribe((s) => {
      this.bbState = s;
      this.cdr.markForCheck();
    });
  }

  get questPct(): number {
    const q = this.progress.quest;
    return q.total ? Math.round((q.done / q.total) * 100) : 0;
  }

  get questLine(): string {
    return `${this.progress.quest.done}/${this.progress.quest.total} · ${this.progress.xpIntoLevel}/${this.progress.xpForLevel} XP`;
  }

  accentFor(action: QuickAction): string {
    return ACCENT_HEX[action.accent];
  }

  openBuddy(): void {
    this.shell.open();
    this.onClose.emit();
  }

  runQuickAction(action: QuickAction): void {
    this.shell.open();
    this.workflows.run(action.workflowId);
    this.onClose.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.onClose.emit();
    }
  }

  trackAction(_: number, action: QuickAction): string {
    return action.workflowId;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
